/**
 * Money is always an integer number of whole rupees. No paise, no floats.
 * Every percentage computation names its rounding explicitly.
 */
export type Rupees = number;

export function assertRupees(n: unknown, what = 'amount'): asserts n is Rupees {
  if (typeof n !== 'number' || !Number.isSafeInteger(n)) throw new RangeError(`${what} must be a whole number of rupees`);
}

export function assertNonNegative(n: unknown, what = 'amount'): asserts n is Rupees {
  assertRupees(n, what);
  if ((n as number) < 0) throw new RangeError(`${what} cannot be negative`);
}

/** ceil(a / b) for non-negative integers, without floating point. */
export function ceilDiv(a: number, b: number): number {
  if (b <= 0) throw new RangeError('divisor must be positive');
  return a >= 0 ? Math.floor((a + b - 1) / b) : -Math.floor(-a / b);
}

/** round(a / b) half-up for non-negative integers. */
export function roundDiv(a: number, b: number): number {
  if (b <= 0) throw new RangeError('divisor must be positive');
  return Math.floor((2 * a + b) / (2 * b));
}

/** pct% of amount, rounded half-up to whole rupees. pct may be a whole-number percentage. */
export function percentOf(amount: Rupees, pct: number): Rupees {
  if (!Number.isInteger(pct)) throw new RangeError('percentage must be a whole number');
  return roundDiv(amount * pct, 100);
}

/** pct% of amount, rounded UP to the next multiple of `step` rupees. */
export function percentCeilTo(amount: Rupees, pct: number, step: number): Rupees {
  if (!Number.isInteger(pct)) throw new RangeError('percentage must be a whole number');
  return ceilDiv(amount * pct, 100 * step) * step;
}

export const sum = (xs: readonly number[]): number => xs.reduce((t, x) => t + x, 0);

/** ₹1,68,000 — Indian digit grouping, U+2212 minus. Independent of the runtime's ICU data. */
export function inr(n: Rupees): string {
  const neg = n < 0;
  const s = String(Math.abs(Math.round(n)));
  let out = s;
  if (s.length > 3) {
    const last3 = s.slice(-3);
    let rest = s.slice(0, -3);
    const groups: string[] = [];
    while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
    if (rest) groups.unshift(rest);
    out = groups.join(',') + ',' + last3;
  }
  return (neg ? '−' : '') + '₹' + out;
}
